import { Location } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	inject,
} from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { PaginationDetails, User, UserParams } from "src/app/models/models";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { sortUserRolesForDisplay } from "src/app/shared/utils/user-role.utils";

@Component({
	selector: "app-user-management",
	templateUrl: "./user-management.component.html",
	styleUrls: ["./user-management.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class UserManagementComponent implements OnInit, OnDestroy, AfterViewInit {
	public columns!: Columns[];
	public configuration!: Config;
	isOnline: boolean = true;

	@ViewChild("userNameTpl", { static: true }) userNameTpl!: TemplateRef<any>;
	@ViewChild("emailTpl", { static: true }) emailTpl!: TemplateRef<any>;
	@ViewChild("roleTpl", { static: true }) roleTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;

	filters?: UserParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	searchQuery: string = "";
	selectedRole: string = "";
	filteredUsers: User[] = [];

	usersSub!: Subscription;
	users: User[] = [];
	routeSub!: Subscription;

	private searchTimeout: any;
	sessionMasterData = this.sharedService.sessionMasterData;
	private snackBar = inject(MatSnackBar);

	constructor(
		private sharedService: SharedService,
		private router: Router,
		private httpService: HttpService,
		private route: ActivatedRoute,
		private loaderService: LoaderService,
		private location: Location,
		private cdr: ChangeDetectorRef,
		private roleService: RoleService,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.USER_MANAGEMENT);
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
			this.cdr.detectChanges();
		});

		this.initTableConfig();
	}

	ngAfterViewInit(): void {
		this.initParams();
	}

	ngOnDestroy(): void {
		if (this.usersSub) {
			this.usersSub.unsubscribe();
		}
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	}

	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;

		this.columns = [
			{
				key: "name",
				title: "Name",
				cellTemplate: this.userNameTpl,
				width: "35%",
			},
			{
				key: "email",
				title: "Email",
				cellTemplate: this.emailTpl,
				width: "40%",
			},
			{
				key: "roles",
				title: "Role",
				cellTemplate: this.roleTpl,
				width: "15%",
			},
			{
				key: "",
				title: "Action",
				cellTemplate: this.actionTpl,
				width: "10%",
			},
		];
	}

	initParams(): void {
		this.routeSub = this.route.queryParams.subscribe((params) => {
			this.updateFilters({
				...this.filters,
				...params,
			});
		});
	}

	initUserList(): void {
		this.loaderService.setLoading(true);

		this.usersSub = this.httpService.usersV2(this.filters).subscribe((result) => {
			const data = result.data;
			const users = data.data as User[];

			const total: number = data.total;

			this.users = users.map((u) => {
				// TODO: check if email outside airasia, then mark as external
				const isEmailExternal = !u.email.includes("airasia.com");

				const hasVDR = u.email.startsWith("vdr_");

				const isExternal = u?.is_external;

				return {
					...u,
					is_external: isExternal,
				};
			});

			const paginationDetails = this.calculatePages(total, this.size);

			this.pagination = {
				limit: this.size,
				offset: this.page,
				count: this.size * paginationDetails.totalPages,
			};

			this.cdr.detectChanges();

			this.loaderService.setLoading(false);
		});
	}

	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const value = event.value;

				const filters = {
					...this.filters,
					page: value.page,
					size: value.limit,
					sortBy: value.sortBy ?? "name",
					sortOrder: value.sortOrder ?? "asc",
				};

				if (this.searchQuery && this.searchQuery.trim() !== "") {
					filters.search = this.searchQuery;
				}

				if (this.selectedRole && this.selectedRole.trim() !== "") {
					filters.roles = this.selectedRole;
				}

				this.updateFilters(filters);
				break;

			default:
				break;
		}
	}

	onSearch(): void {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = setTimeout(() => {
			const filters: any = {
				...this.filters,
				page: 1,
				size: this.size,
				sortBy: this.filters?.sortBy ?? "name",
				sortOrder: this.filters?.sortOrder ?? "asc",
			};

			if (this.searchQuery && this.searchQuery.trim()) {
				filters.search = this.searchQuery;
			} else {
				delete filters.search;
			}

			if (this.selectedRole && this.selectedRole.trim() && this.selectedRole !== "All") {
				filters.roles = this.selectedRole;
			} else {
				delete filters.roles;
			}

			this.updateFilters(filters);
		}, 300);
	}

	onRoleFilter(): void {
		const filters: any = {
			...this.filters,
			page: 1,
			size: this.size,
			sortBy: this.filters?.sortBy ?? "name",
			sortOrder: this.filters?.sortOrder ?? "asc",
		};

		if (this.searchQuery && this.searchQuery.trim()) {
			filters.search = this.searchQuery;
		} else {
			delete filters.search;
		}

		if (this.selectedRole && this.selectedRole.trim() && this.selectedRole !== "All") {
			filters.roles = this.selectedRole;
		} else {
			delete filters.roles;
		}

		this.updateFilters(filters);
	}

	editUser(id: string): void {
		this.router.navigate([RouteType.EDIT_USER.replace(":id", id)]);
	}

	deleteUser(id: string): void {
		if (confirm("Are you sure you want to delete this user?")) {
			this.loaderService.setLoading(true);

			this.httpService.deleteUsers(id).subscribe({
				next: () => {
					this.loaderService.setLoading(false);

					// Check if current page will be empty after deletion
					const currentPageItemCount = this.users.length;
					if (currentPageItemCount === 1 && this.page > 1) {
						// If deleting the last item on current page and not on first page, go to previous page
						this.updateFilters({
							...this.filters,
							page: this.page - 1,
						});
					} else {
						// Otherwise, reload current page
						this.initUserList();
					}
				},
				error: (error) => {
					console.error("Error deleting user:", error);
					this.loaderService.setLoading(false);
					this.showErrorSnackbar("Cannot delete user");
				},
			});
		}
	}

	updateFilters(filters: UserParams) {
		this.filters = filters;

		if (!isEmpty(filters?.size)) {
			this.configuration.rows = parseInt(`${this.size}`);
		}
		this.filters = {
			...filters,
		};

		this.updateUrl();
		this.initUserList();
	}

	updateUrl(): void {
		const url = this.router.serializeUrl(
			this.router.createUrlTree([RouteType.USER_MANAGEMENT], {
				queryParams: this.filters,
			}),
		);

		this.location.go(url);
	}

	createNewUser(): void {
		this.router.navigate([RouteType.NEW_USER]);
	}

	get constant() {
		return CONSTANT;
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? this.constant.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? this.constant.PAGINATION.DEFAULT_SIZE}`);
	}

	get rolesOptions() {
		return sortUserRolesForDisplay(this.sessionMasterData?.roles);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage);
		const lastPageSize = totalDataCount % rowsPerPage;

		return { totalPages, lastPageSize };
	}

	private showErrorSnackbar(message: string): void {
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: { message } as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
	}
}
